import { useCallback, useContext, useEffect, useMemo } from 'react';
import CustomerWrapper from '~/apps/smartsend/components/smartsend/CustomerWrapper';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { InitialiseCustomerAction } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { IsDefaultAddress } from '~/apps/smartsend/lib/helpers/IsDefaultAddress';
import { Customer } from '~/lib/api/smartsend/types';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import SearchInitialiser from './search-initialiser';
import { useAuth } from 'react-oidc-context';
import { ConfigSetting, useTenant } from '@parcel2go/npm-p2g-app';
import { SmartSendProvider } from '~/lib/consts/providers';

interface props {
  isXs: boolean;
  children?: React.ReactNode;
  silent?: boolean;
}

export default function RequireCustomer({ isXs, silent, children }: props) {
  const { dispatch, state } = useContext(SmartSendContext);
  const { getCustomers } = useSmartSendApi();
  const auth = useAuth();
  const { settings } = useTenant();

  const initialCustomer: Customer = useMemo(() => {
    const customerName = auth?.user?.profile?.name as string;
    return {
      CountryIsoCode: settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY],
      CountrySubdivision: undefined,
      LegacyCountryId: 219,
      AccountName: 'Default',
      Name: customerName?.length < 25 ? customerName : '',
      Postcode: '',
      Email: auth?.user?.profile?.email || auth?.user?.profile?.preferred_username || '',
      Phone: auth?.user?.profile?.phone_number || '',
      Town: '',
      AddressLine1: '',
      AddressLine2: '',
      Provider: SmartSendProvider,
      VatNumber: '',
      Meta: {}
    };
  }, [settings, auth]);

  const setCustomer = useCallback(
    (customer: Customer) => {
      const load = async () => {
        dispatch(new InitialiseCustomerAction(customer));
      };
      load();
    },
    [dispatch]
  );

  useEffect(() => {
    const getSilent = async () => {
      const customers = await getCustomers();
      if (customers && customers.success) {
        if (customers.result && customers.result.Customers?.length > 0) {
          setCustomer(customers.result.Customers.find(IsDefaultAddress) || customers.result.Customers[0]);
        }
      }
    };

    if (silent) {
      getSilent();
    }
  }, [silent, getCustomers, setCustomer]);

  if (silent || state.customerInitialised) {
    return <SearchInitialiser>{children}</SearchInitialiser>;
  }

  return (
    <CustomerWrapper isXs={isXs} silent={silent} setCustomer={setCustomer} initialCustomer={initialCustomer}>
      {children}
    </CustomerWrapper>
  );
}
